import { useCallback, useEffect, useRef, useState } from 'react'
import kitApi, {
    CreateKitScreeningJobReq,
    KitScreeningJobDto,
    UpdateKitScreeningJobReq,
} from '@/api/kit/KitApi'
import companyApi from '@/api/company/CompanyApi'
import tenantApi from '@/api/tenant/TenantApi'
import { CompanyCsSearchResultDto } from '@/api/company/models/companyCs/CompanyCsDtos'
import Button from '@/components/Button'
import Modal from '@/components/Modal'
import CompanySearchModal from '@/uiV2/company/search/CompanySearchModal'
import ToggleSwitch3 from '../ToggleSwitch3'
import KitPaginationControls from './KitPaginationControls'
import RubricImportModal from './RubricImportModal'
import RubricViewModal from './RubricViewModal'
import LinkCopyButton from '@/uiV2/jobScreeners/components/LinkCopyButton'
import { buildApplyUrl } from '@/uiV2/jobScreeners/utils'

const PAGE_SIZE = 50

type CountryOption = {
    code: string
    name: string
    label: string
}

const PRIORITY_COUNTRIES = ['US', 'UK'] as const
const PRIORITY_COUNTRY_SET = new Set<string>(PRIORITY_COUNTRIES)
const EXCLUDED_COUNTRY_CODES = new Set<string>(['GB'])

const buildCountryOptions = (): CountryOption[] => {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' })
    const countryNames = new Map<string, string>()

    for (let i = 0; i < 26 * 26; i++) {
        const code =
            String.fromCharCode(65 + Math.floor(i / 26)) +
            String.fromCharCode(65 + (i % 26))
        const name = displayNames.of(code)
        if (!name || name === code || EXCLUDED_COUNTRY_CODES.has(code)) {
            continue
        }
        countryNames.set(code, name)
    }

    countryNames.set('US', countryNames.get('US') ?? 'United States')
    countryNames.set('UK', countryNames.get('UK') ?? 'United Kingdom')

    const priorityOptions = PRIORITY_COUNTRIES.map((code) => {
        const name = countryNames.get(code) ?? code
        return {
            code,
            name,
            label: `${code} - ${name}`,
        }
    })

    const remainingOptions: CountryOption[] = []
    countryNames.forEach((name, code) => {
        if (PRIORITY_COUNTRY_SET.has(code)) {
            return
        }
        remainingOptions.push({
            code,
            name,
            label: `${code} - ${name}`,
        })
    })

    remainingOptions.sort((a, b) => a.code.localeCompare(b.code))

    return [...priorityOptions, ...remainingOptions]
}

const COUNTRY_OPTIONS = buildCountryOptions()
const COMPANY_LOOKUP_DEBOUNCE_MS = 300
const SCREENING_TABLE_CLASS =
    'main-table w-full min-w-[1180px] text-[11px] leading-tight'
const SCREENING_HEADER_CELL_CLASS =
    'px-2 py-1 text-left align-top whitespace-normal'
const SCREENING_CELL_CLASS = 'px-2 py-1 align-top'
const TABLE_MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
]
const TABLE_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

type ScreeningJobFormState = {
    id?: string
    country: string
    jobTitle: string
    clientCompanyId: string
    clientFreehandName: string
    jobNumber: string
    agentId: string
    urlId: string
    isActive: boolean
    tenantId: string
}

const createEmptyFormState = (): ScreeningJobFormState => ({
    country: '',
    jobTitle: '',
    clientCompanyId: '',
    clientFreehandName: '',
    jobNumber: '',
    agentId: '',
    urlId: '',
    isActive: true,
    tenantId: '',
})

const optionalText = (value: string): string | undefined => {
    const trimmed = value.trim()
    return trimmed ? trimmed : undefined
}

const validateForm = (formState: ScreeningJobFormState): string | null => {
    const country = formState.country.trim()
    if (!country) return 'Country is required.'
    if (country.length !== 2) return 'Country must be a 2-letter ISO code.'
    if (!formState.jobTitle.trim()) return 'Job title is required.'
    if (!formState.agentId.trim()) return 'Agent id is required.'
    if (!formState.urlId.trim()) return 'Url id is required.'

    const companyId = formState.clientCompanyId.trim()
    const freehand = formState.clientFreehandName.trim()
    if (companyId && freehand) {
        return 'Provide either a client company or a freehand name, not both.'
    }
    if (!companyId && !freehand) {
        return 'Provide a client company or a freehand name.'
    }

    return null
}

const buildCreatePayload = (
    formState: ScreeningJobFormState,
): CreateKitScreeningJobReq => ({
    country: formState.country.trim().toUpperCase(),
    jobTitle: formState.jobTitle.trim(),
    clientCompanyId: optionalText(formState.clientCompanyId),
    clientFreehandName: optionalText(formState.clientFreehandName),
    jobNumber: optionalText(formState.jobNumber),
    agentId: formState.agentId.trim(),
    urlId: formState.urlId.trim(),
    isActive: formState.isActive,
    tenantId: optionalText(formState.tenantId),
})

const formatTableDateTime = (value: string): string => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    const pad = (num: number) => num.toString().padStart(2, '0')
    const weekday = TABLE_WEEKDAYS[date.getDay()]
    const day = pad(date.getDate())
    const month = TABLE_MONTHS[date.getMonth()]
    const year = date.getFullYear()
    const hours = pad(date.getHours())
    const minutes = pad(date.getMinutes())
    const seconds = pad(date.getSeconds())
    return `${weekday} ${day} ${month} ${year} ${hours}:${minutes}:${seconds}`
}

export default function KitScreeningJobsTab(): React.JSX.Element {
    const [jobs, setJobs] = useState<KitScreeningJobDto[]>([])
    const [pageNumber, setPageNumber] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasRequested, setHasRequested] = useState(false)
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
    const [formState, setFormState] = useState<ScreeningJobFormState>(
        createEmptyFormState(),
    )
    const [clientCompanyName, setClientCompanyName] = useState('')
    const [isCompanyNameLoading, setIsCompanyNameLoading] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [togglingId, setTogglingId] = useState<string | null>(null)
    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false)
    const [rubricImportJob, setRubricImportJob] =
        useState<KitScreeningJobDto | null>(null)
    const [rubricViewJob, setRubricViewJob] =
        useState<KitScreeningJobDto | null>(null)
    const [tenants, setTenants] = useState<
        { id: string; name: string; isPersonal: boolean }[]
    >([])
    const [showJobSeekers, setShowJobSeekers] = useState(false)
    const [, setCompanyNameCacheVersion] = useState(0)
    const companyNameCacheRef = useRef(new Map<string, string>())
    const companyLookupIdRef = useRef<string | null>(null)
    const companyTableLookupRef = useRef(new Set<string>())

    const fetchJobs = useCallback(async (page: number) => {
        try {
            setIsLoading(true)
            setError(null)
            const res = await kitApi.getScreeningJobs({
                pageNumber: page,
                perPage: PAGE_SIZE,
            })
            setJobs(res.jobs)
            setHasMore(res.hasMore)
        } catch (err) {
            console.error('Failed to load Kit screening jobs', err)
            setError('Unable to load Kit screening jobs.')
        } finally {
            setIsLoading(false)
            setHasRequested(true)
        }
    }, [])

    useEffect(() => {
        void fetchJobs(pageNumber)
    }, [fetchJobs, pageNumber])

    useEffect(() => {
        void (async () => {
            try {
                const res = await tenantApi.getAll({
                    includePersonal: showJobSeekers,
                })
                setTenants(
                    res.tenants.map((t) => ({
                        id: t.id,
                        name: t.name,
                        isPersonal: t.isPersonal,
                    })),
                )
            } catch {
                // Swallow — tenant list is optional.
            }
        })()
    }, [showJobSeekers])

    const openCreateForm = () => {
        setFormMode('create')
        setFormState(createEmptyFormState())
        setClientCompanyName('')
        setIsCompanyNameLoading(false)
        setFormError(null)
        setIsFormOpen(true)
    }

    const openEditForm = (job: KitScreeningJobDto) => {
        const cachedName = job.clientCompanyId
            ? (companyNameCacheRef.current.get(job.clientCompanyId) ?? '')
            : ''
        setFormMode('edit')
        setFormState({
            id: job.id,
            country: job.country,
            jobTitle: job.jobTitle,
            clientCompanyId: job.clientCompanyId ?? '',
            clientFreehandName: job.clientFreehandName ?? '',
            jobNumber: job.jobNumber ?? '',
            agentId: job.agentId,
            urlId: job.urlId,
            isActive: job.isActive,
            tenantId: '',
        })
        setClientCompanyName(cachedName)
        setIsCompanyNameLoading(false)
        setFormError(null)
        setIsFormOpen(true)
    }

    const closeForm = () => {
        if (isSubmitting) return
        setIsFormOpen(false)
    }

    const handleCompanyConfirm = (companyIds: string[]) => {
        const [companyId] = companyIds
        if (companyId) {
            setFormState((prev) => ({
                ...prev,
                clientCompanyId: companyId,
                clientFreehandName: '',
            }))
        } else {
            setClientCompanyName('')
        }
        setIsCompanyModalOpen(false)
    }

    const handleCompanyConfirmCompanies = useCallback(
        (companies: CompanyCsSearchResultDto[]) => {
            const [company] = companies
            if (!company) {
                setClientCompanyName('')
                return
            }
            const name = company.name?.trim() || ''
            if (name) {
                companyNameCacheRef.current.set(company.id, name)
            }
            setClientCompanyName(name)
        },
        [],
    )

    const handleSubmit = useCallback(async () => {
        const validationError = validateForm(formState)
        if (validationError) {
            setFormError(validationError)
            return
        }

        setIsSubmitting(true)
        setFormError(null)

        try {
            const payload = buildCreatePayload(formState)
            if (formMode === 'create') {
                await kitApi.createScreeningJob(payload)
            } else {
                const updatePayload: UpdateKitScreeningJobReq = {
                    ...payload,
                    id: formState.id!,
                    isActive: formState.isActive,
                }
                await kitApi.updateScreeningJob(updatePayload)
            }
            await fetchJobs(pageNumber)
            setIsFormOpen(false)
        } catch (err: any) {
            console.error('Failed to save screening job', err)
            setFormError(
                err instanceof Error
                    ? err.message
                    : 'Unable to save screening job.',
            )
        } finally {
            setIsSubmitting(false)
        }
    }, [fetchJobs, formMode, formState, pageNumber])

    useEffect(() => {
        const companyId = formState.clientCompanyId.trim()
        if (!companyId) {
            companyLookupIdRef.current = null
            setClientCompanyName('')
            setIsCompanyNameLoading(false)
            return
        }

        const cachedName = companyNameCacheRef.current.get(companyId)
        if (cachedName) {
            setClientCompanyName(cachedName)
            setIsCompanyNameLoading(false)
            return
        }

        setClientCompanyName('')
        const handle = window.setTimeout(async () => {
            companyLookupIdRef.current = companyId
            setIsCompanyNameLoading(true)
            try {
                const res = await companyApi.searchCompanyCs({
                    q: companyId,
                    searchMode: 'id',
                    pageNumber: 1,
                    perPage: 1,
                })
                if (companyLookupIdRef.current !== companyId) return
                const match =
                    res.results.find((result) => result.id === companyId) ??
                    res.results[0]
                const name = match?.name?.trim() || ''
                if (name) {
                    companyNameCacheRef.current.set(companyId, name)
                    setCompanyNameCacheVersion((version) => version + 1)
                }
                setClientCompanyName(name)
            } catch {
                if (companyLookupIdRef.current === companyId) {
                    setClientCompanyName('')
                }
            } finally {
                if (companyLookupIdRef.current === companyId) {
                    setIsCompanyNameLoading(false)
                }
            }
        }, COMPANY_LOOKUP_DEBOUNCE_MS)

        return () => {
            window.clearTimeout(handle)
        }
    }, [formState.clientCompanyId])

    useEffect(() => {
        if (jobs.length === 0) return

        let cancelled = false
        const missingCompanyIds = Array.from(
            new Set(
                jobs
                    .map((job) => job.clientCompanyId)
                    .filter((id): id is string => Boolean(id?.trim())),
            ),
        ).filter((id) => !companyNameCacheRef.current.has(id))

        if (missingCompanyIds.length === 0) return

        const fetchCompanyName = async (companyId: string) => {
            if (companyTableLookupRef.current.has(companyId)) return
            companyTableLookupRef.current.add(companyId)
            try {
                const res = await companyApi.searchCompanyCs({
                    q: companyId,
                    searchMode: 'id',
                    pageNumber: 1,
                    perPage: 1,
                })
                if (cancelled) return
                const match =
                    res.results.find((result) => result.id === companyId) ??
                    res.results[0]
                const name = match?.name?.trim() || ''
                if (name) {
                    companyNameCacheRef.current.set(companyId, name)
                    setCompanyNameCacheVersion((version) => version + 1)
                }
            } catch {
                // Swallow lookup errors and keep the ID visible.
            } finally {
                companyTableLookupRef.current.delete(companyId)
            }
        }

        void (async () => {
            for (const companyId of missingCompanyIds) {
                if (cancelled) return
                await fetchCompanyName(companyId)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [jobs])

    const handleToggleActive = useCallback(
        async (job: KitScreeningJobDto) => {
            setTogglingId(job.id)
            setError(null)
            try {
                await kitApi.updateScreeningJob({
                    id: job.id,
                    country: job.country,
                    jobTitle: job.jobTitle,
                    clientCompanyId: job.clientCompanyId ?? undefined,
                    clientFreehandName: job.clientFreehandName ?? undefined,
                    jobNumber: job.jobNumber ?? undefined,
                    agentId: job.agentId,
                    urlId: job.urlId,
                    isActive: !job.isActive,
                })
                await fetchJobs(pageNumber)
            } catch (err) {
                console.error('Failed to update job status', err)
                setError('Unable to update job status.')
            } finally {
                setTogglingId(null)
            }
        },
        [fetchJobs, pageNumber],
    )

    const clientCompanyDisplayName = formState.clientCompanyId
        ? clientCompanyName ||
          (isCompanyNameLoading ? 'Loading company...' : 'Unknown company')
        : ''

    const tableRows = jobs.map((job) => {
        const statusClasses = job.isActive
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-200 text-gray-600'
        const companyName = job.clientCompanyId
            ? (companyNameCacheRef.current.get(job.clientCompanyId) ?? '')
            : ''
        const clientContent = job.clientCompanyId
            ? {
                  value: companyName || job.clientCompanyId,
                  label: companyName
                      ? `ID: ${job.clientCompanyId}`
                      : 'Company ID',
              }
            : job.clientFreehandName
              ? {
                    value: job.clientFreehandName,
                    label: 'Freehand',
                }
              : null
        const applyUrl = buildApplyUrl(job.urlId)

        return (
            <tr key={job.id}>
                <td className={SCREENING_CELL_CLASS}>
                    <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusClasses}`}
                    >
                        {job.isActive ? 'Active' : 'Inactive'}
                    </span>
                </td>
                <td className={SCREENING_CELL_CLASS}>{job.id}</td>
                <td className={SCREENING_CELL_CLASS}>{job.country}</td>
                <td className={SCREENING_CELL_CLASS}>{job.jobTitle}</td>
                <td className={SCREENING_CELL_CLASS}>
                    {clientContent ? (
                        <div className="flex flex-col">
                            <span>{clientContent.value}</span>
                            <span className="text-xs text-gray-500">
                                {clientContent.label}
                            </span>
                        </div>
                    ) : (
                        <span className="text-gray-500">-</span>
                    )}
                </td>
                <td className={SCREENING_CELL_CLASS}>{job.jobNumber || '-'}</td>
                <td className={SCREENING_CELL_CLASS}>{job.agentId}</td>
                <td className={SCREENING_CELL_CLASS}>{job.urlId}</td>
                <td className={SCREENING_CELL_CLASS}>
                    {applyUrl ? (
                        <LinkCopyButton url={applyUrl} maxDisplayLength={50} />
                    ) : (
                        <span className="text-gray-500">-</span>
                    )}
                </td>
                <td className={SCREENING_CELL_CLASS}>
                    {job.rubricSectionCount === 0 ? (
                        <span className="text-gray-400">None</span>
                    ) : (
                        <button
                            type="button"
                            className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 hover:bg-green-200 cursor-pointer"
                            onClick={() => setRubricViewJob(job)}
                        >
                            Created
                        </button>
                    )}
                </td>
                <td className={SCREENING_CELL_CLASS}>
                    {formatTableDateTime(job.createdAt)}
                </td>
                <td className={SCREENING_CELL_CLASS}>
                    {formatTableDateTime(job.updatedAt)}
                </td>
                <td className={SCREENING_CELL_CLASS}>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={() => openEditForm(job)}
                            enabled={!isLoading && togglingId !== job.id}
                        >
                            Edit
                        </Button>
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={() => void handleToggleActive(job)}
                            enabled={!isLoading && togglingId !== job.id}
                        >
                            {job.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={() => setRubricImportJob(job)}
                            enabled={!isLoading && togglingId !== job.id}
                        >
                            Import Rubric
                        </Button>
                    </div>
                </td>
            </tr>
        )
    })

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-700">
                    Screening Jobs
                </h3>
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        style="secondary"
                        size="small"
                        onClick={() => void fetchJobs(pageNumber)}
                        enabled={!isLoading}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="button"
                        style="primary"
                        size="small"
                        onClick={openCreateForm}
                        enabled={!isLoading}
                    >
                        New screening job
                    </Button>
                </div>
            </div>
            {error && (
                <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}
            {isLoading && !hasRequested ? (
                <p className="mt-4 text-sm text-gray-500">
                    Loading screening jobs...
                </p>
            ) : jobs.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">
                    {hasRequested
                        ? 'No screening jobs found.'
                        : 'Select this tab to load screening jobs.'}
                </p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className={SCREENING_TABLE_CLASS}>
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wide">
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Status
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Job Id
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Country
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Job Title
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Client
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Job Number
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Agent Id
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Url Id
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Apply URL
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Rubric
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Created At
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Updated At
                                    </th>
                                    <th className={SCREENING_HEADER_CELL_CLASS}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>{tableRows}</tbody>
                        </table>
                    </div>
                    <KitPaginationControls
                        pageNumber={pageNumber}
                        itemCount={jobs.length}
                        perPage={PAGE_SIZE}
                        hasMore={hasMore}
                        isLoading={isLoading}
                        onPrevious={() =>
                            setPageNumber((page) => Math.max(1, page - 1))
                        }
                        onNext={() => setPageNumber((page) => page + 1)}
                    />
                </>
            )}

            {isFormOpen && (
                <Modal
                    title={
                        formMode === 'create'
                            ? 'Create Screening Job'
                            : 'Edit Screening Job'
                    }
                    onClose={closeForm}
                    size="large"
                    allowUserToClose={!isSubmitting}
                >
                    <form
                        className="flex flex-col gap-4"
                        onSubmit={(event) => {
                            event.preventDefault()
                            void handleSubmit()
                        }}
                    >
                        {formError && (
                            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                {formError}
                            </div>
                        )}
                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="text-sm font-semibold text-gray-700">
                                    Country (ISO 2)
                                </label>
                                <select
                                    className="form-input mt-1 w-full"
                                    value={formState.country}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            country: event.target.value,
                                        }))
                                    }
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select a country</option>
                                    {COUNTRY_OPTIONS.map((option) => (
                                        <option
                                            key={option.code}
                                            value={option.code}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700">
                                    Job Title
                                </label>
                                <input
                                    className="form-input mt-1 w-full"
                                    value={formState.jobTitle}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            jobTitle: event.target.value,
                                        }))
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700">
                                    Agent Id
                                </label>
                                <input
                                    className="form-input mt-1 w-full"
                                    value={formState.agentId}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            agentId: event.target.value,
                                        }))
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700">
                                    Url Id
                                </label>
                                <input
                                    className="form-input mt-1 w-full"
                                    value={formState.urlId}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            urlId: event.target.value,
                                        }))
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700">
                                    Job Number (optional)
                                </label>
                                <input
                                    className="form-input mt-1 w-full"
                                    value={formState.jobNumber}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            jobNumber: event.target.value,
                                        }))
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-semibold text-gray-700">
                                    Active
                                </label>
                                <input
                                    type="checkbox"
                                    className="form-checkbox"
                                    checked={formState.isActive}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            isActive: event.target.checked,
                                        }))
                                    }
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700">
                                    Tenant (optional)
                                </label>
                                <select
                                    className="form-input mt-1 w-full"
                                    value={formState.tenantId}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            tenantId: event.target.value,
                                        }))
                                    }
                                    disabled={isSubmitting}
                                >
                                    <option value="">-- No tenant --</option>
                                    {tenants.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name}
                                            {t.isPersonal
                                                ? ' (Job Seeker)'
                                                : ''}
                                        </option>
                                    ))}
                                </select>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="text-sm text-gray-600">
                                        Show Job Seekers
                                    </span>
                                    <ToggleSwitch3
                                        value={showJobSeekers}
                                        onChange={setShowJobSeekers}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="text-sm font-semibold text-gray-700">
                                    Client Company Id
                                </label>
                                <div className="mt-1 flex flex-wrap gap-2">
                                    <input
                                        className="form-input flex-1"
                                        value={clientCompanyDisplayName}
                                        placeholder="Select a company"
                                        readOnly={true}
                                        disabled={isSubmitting}
                                    />
                                    <Button
                                        type="button"
                                        style="secondary"
                                        onClick={() =>
                                            setIsCompanyModalOpen(true)
                                        }
                                        enabled={!isSubmitting}
                                    >
                                        Find company
                                    </Button>
                                    {formState.clientCompanyId && (
                                        <Button
                                            type="button"
                                            style="mild-danger"
                                            onClick={() => {
                                                setClientCompanyName('')
                                                setFormState((prev) => ({
                                                    ...prev,
                                                    clientCompanyId: '',
                                                }))
                                            }}
                                            enabled={!isSubmitting}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                {formState.clientCompanyId && (
                                    <p className="mt-1 text-xs text-gray-500">
                                        Company ID: {formState.clientCompanyId}
                                    </p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">
                                    Use the company finder to select a company.
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700">
                                    Client Freehand Name
                                </label>
                                <input
                                    className="form-input mt-1 w-full"
                                    value={formState.clientFreehandName}
                                    placeholder="Company name if not found"
                                    onChange={(event) => {
                                        const nextValue = event.target.value
                                        if (nextValue.trim()) {
                                            setClientCompanyName('')
                                        }
                                        setFormState((prev) => ({
                                            ...prev,
                                            clientFreehandName: nextValue,
                                            clientCompanyId: nextValue.trim()
                                                ? ''
                                                : prev.clientCompanyId,
                                        }))
                                    }}
                                    disabled={isSubmitting}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Provide this only if you cannot find a
                                    company above.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                style="secondary"
                                onClick={closeForm}
                                enabled={!isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                style="primary"
                                enabled={!isSubmitting}
                            >
                                {isSubmitting ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}

            {isCompanyModalOpen && (
                <CompanySearchModal
                    onClose={() => setIsCompanyModalOpen(false)}
                    onConfirm={handleCompanyConfirm}
                    onConfirmCompanies={handleCompanyConfirmCompanies}
                    multiSelect={false}
                />
            )}

            {rubricViewJob && (
                <RubricViewModal
                    screeningJobId={rubricViewJob.id}
                    jobTitle={rubricViewJob.jobTitle}
                    onClose={() => setRubricViewJob(null)}
                />
            )}

            {rubricImportJob && (
                <RubricImportModal
                    screeningJobId={rubricImportJob.id}
                    jobTitle={rubricImportJob.jobTitle}
                    hasExistingRubric={rubricImportJob.rubricSectionCount > 0}
                    onClose={() => setRubricImportJob(null)}
                    onSaved={() => {
                        setRubricImportJob(null)
                        void fetchJobs(pageNumber)
                    }}
                />
            )}
        </div>
    )
}
